import { useState, useEffect, useCallback } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import DataTable from '../../components/admin/DataTable';
import Badge from '../../components/admin/Badge';
import StatCard from '../../components/admin/StatCard';
import Pagination from '../../components/admin/Pagination';

const TYPE_LABELS = {
  verification: 'Verification',
  password_reset: 'Password Reset',
  order_confirmation: 'Order Confirmation',
  payment_failure: 'Payment Failure',
  payment_restoration: 'Payment Restoration',
  waitlist_available: 'Waitlist — Available',
  custom_admin: 'Custom (Admin)',
  activation: 'Activation',
  domain_instructions: 'Domain Instructions',
  suspension: 'Suspension',
  resumed: 'Resumed',
  rejected: 'Rejected',
  payment_auth: 'Payment Auth',
  service_removed: 'Service Removed',
  price_update: 'Price Update',
  upgrade_pending: 'Upgrade Pending',
  plan_upgrade: 'Plan Upgrade',
  plan_downgrade: 'Plan Downgrade',
  support_ticket: 'Support Ticket',
  drip: 'Drip / Onboarding',
  other: 'Other',
};

const EMAIL_TYPES = Object.keys(TYPE_LABELS);

function EmailPreviewModal({ log, onClose, onRetry, retrying }) {
  if (!log) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--admin-card)',
          border: '1px solid var(--admin-border)',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '780px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--admin-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--admin-text-primary)', marginBottom: '4px' }}>{log.subject}</div>
            <div style={{ fontSize: '12px', color: 'var(--admin-text-muted)', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <span><strong>To:</strong> {log.recipient}</span>
              <span><strong>Type:</strong> {TYPE_LABELS[log.email_type] || log.email_type}</span>
              <span><strong>Sent:</strong> {new Date(log.created_at).toLocaleString()}</span>
              <span><strong>Status:</strong> <Badge variant={log.status === 'sent' ? 'active' : 'suspended'}>{log.status}</Badge></span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
            {log.status === 'failed' && onRetry && (
              <button
                className="admin-btn admin-btn--sm"
                style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24' }}
                onClick={() => onRetry(log)}
                disabled={retrying}
              >
                <i className={`fa-solid ${retrying ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} style={{ marginRight: '4px' }} />
                {retrying ? 'Sending…' : 'Retry'}
              </button>
            )}
            <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={onClose}>
              <i className="fa-solid fa-times" />
            </button>
          </div>
        </div>

        {/* Email Preview */}
        <div style={{ flex: 1, overflow: 'auto', background: '#f0f2f4' }}>
          {log.html_body ? (
            <iframe
              srcDoc={log.html_body}
              title="Email Preview"
              style={{ width: '100%', height: '100%', minHeight: '500px', border: 'none', display: 'block' }}
              sandbox="allow-same-origin"
            />
          ) : log.error_message ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', marginBottom: '12px', color: 'var(--admin-error)' }}><i className="fa-solid fa-circle-xmark" /></div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--admin-error)', marginBottom: '8px' }}>Email failed to send</div>
              <div style={{ fontSize: '12px', color: 'var(--admin-text-muted)', fontFamily: 'monospace', background: 'var(--admin-card)', padding: '12px', borderRadius: '6px', textAlign: 'left' }}>{log.error_message}</div>
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--admin-text-muted)' }}>
              <i className="fa-solid fa-envelope-open" style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.4 }} />
              <div style={{ fontSize: '13px' }}>No preview available for this email</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const QUEUE_STATUS_COLORS = { pending: 'pending', processing: 'info', sent: 'active', failed: 'suspended' };

function AdminEmailLogs({ apiFetch, toast }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [reconciling, setReconciling] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [retrying, setRetrying] = useState(null); // id of email being retried

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 50 });
      if (search) params.set('search', search);
      if (typeFilter) params.set('email_type', typeFilter);
      if (statusFilter) params.set('status', statusFilter);
      const res = await apiFetch(`/api/admin/email-logs?${params}`);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setLogs(data.logs || []);
      setStats(data.stats || null);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setTotalPages(data.totalPages || 1);
    } catch { toast.error('Error', 'Failed to load email logs'); }
    finally { setLoading(false); }
  }, [apiFetch, search, typeFilter, statusFilter, toast]);

  const reconcilePayments = async () => {
    setReconciling(true);
    try {
      const res = await apiFetch('/api/admin/payments/reconcile-now', { method: 'POST' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed');
      toast.success('Reconcile complete', `${d.restored || 0} restoration(s) recovered, ${d.cleared || 0} cleared (of ${d.checked || 0} checked)`);
      load(1);
    } catch (e) { toast.error('Error', e.message || 'Reconcile failed'); }
    finally { setReconciling(false); }
  };

  useEffect(() => { load(1); }, [search, typeFilter, statusFilter]);

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchInput); };

  const openPreview = async (row) => {
    setPreviewLoading(true);
    setPreview(row); // show modal immediately with metadata
    try {
      const res = await apiFetch(`/api/admin/email-logs/${row.id}`);
      if (res.ok) setPreview(await res.json());
    } catch { toast.error('Error', 'Failed to load email preview'); }
    finally { setPreviewLoading(false); }
  };

  const handleRetry = async (row, e) => {
    if (e) e.stopPropagation();
    setRetrying(row.id);
    try {
      const res = await apiFetch(`/api/admin/email-logs/${row.id}/retry`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed');
      toast.success('Retried', 'Email has been resent successfully');
      load(page);
      if (preview?.id === row.id) setPreview(null);
    } catch (err) {
      toast.error('Retry failed', err.message);
    } finally {
      setRetrying(null);
    }
  };

  const columns = [
    { key: 'created_at', header: 'Date', width: '150px', sortable: true,
      render: row => <span style={{ fontSize: '12px', color: 'var(--admin-text-muted)' }}>{new Date(row.created_at).toLocaleString()}</span> },
    { key: 'recipient', header: 'Recipient', sortable: true },
    { key: 'email_type', header: 'Type', width: '160px',
      render: row => <Badge variant="info">{TYPE_LABELS[row.email_type] || row.email_type}</Badge> },
    { key: 'subject', header: 'Subject',
      render: row => <span style={{ fontSize: '12px' }}>{row.subject}</span> },
    { key: 'status', header: 'Status', width: '90px',
      render: row => <Badge variant={row.status === 'sent' ? 'active' : 'suspended'}>{row.status}</Badge> },
    { key: 'error_message', header: 'Error', width: '200px',
      render: row => row.error_message
        ? <span style={{ fontSize: '11px', color: 'var(--admin-error)', fontFamily: 'monospace' }} title={row.error_message}>{row.error_message.slice(0, 60)}{row.error_message.length > 60 ? '…' : ''}</span>
        : <span style={{ color: 'var(--admin-text-muted)', fontSize: '12px' }}>—</span> },
    { key: 'actions', header: '', width: '140px',
      render: row => (
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={e => { e.stopPropagation(); openPreview(row); }}
            title="Preview email"
          >
            <i className="fa-solid fa-eye" />
          </button>
          {row.status === 'failed' && (
            <button
              className="admin-btn admin-btn--sm"
              style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24' }}
              onClick={e => handleRetry(row, e)}
              disabled={retrying === row.id}
              title="Retry sending this email"
            >
              <i className={`fa-solid ${retrying === row.id ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} style={{ marginRight: '4px' }} />
              {retrying === row.id ? 'Sending…' : 'Retry'}
            </button>
          )}
        </div>
      )},
  ];

  return (
    <>
      <SectionHeader
        title="Email Logs"
        description="History of all emails sent from the system"
        actions={
          <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={reconcilePayments} disabled={reconciling} title="Catch payment-failure / restoration emails missed by dropped Stripe webhooks">
            <i className={`fa-solid ${reconciling ? 'fa-spinner fa-spin' : 'fa-rotate'}`} style={{ marginRight: 6 }} />
            {reconciling ? 'Reconciling…' : 'Reconcile payments'}
          </button>
        }
      />
      {/* Stats */}
      {stats && (
        <div className="admin-stats-grid" style={{ marginBottom: '20px' }}>
          <StatCard label="Total Sent" value={stats.total ?? '—'} icon="fa-solid fa-envelope" />
          <StatCard label="Successful" value={stats.sent ?? '—'} icon="fa-solid fa-circle-check" />
          <StatCard label="Failed" value={stats.failed ?? '—'} icon="fa-solid fa-circle-xmark" variant={stats.failed > 0 ? 'danger' : undefined} />
          <StatCard label="Last 24h" value={stats.last_24h ?? '—'} icon="fa-solid fa-clock" />
          <StatCard label="Last 7 days" value={stats.last_7d ?? '—'} icon="fa-solid fa-calendar-week" />
        </div>
      )}

      {/* Filters */}
      <div className="admin-card" style={{ marginBottom: '16px', padding: '12px 16px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '6px', flex: 1, minWidth: '200px' }}>
          <input
            className="admin-input"
            placeholder="Search recipient, subject, type…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ flex: 1, fontSize: '13px' }}
          />
          <button type="submit" className="admin-btn admin-btn--sm admin-btn--primary"><i className="fa-solid fa-search" /></button>
          {search && <button type="button" className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => { setSearch(''); setSearchInput(''); }}><i className="fa-solid fa-times" /></button>}
        </form>

        <select className="admin-input" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ width: 'auto', fontSize: '13px' }}>
          <option value="">All types</option>
          {EMAIL_TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>

        <select className="admin-input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 'auto', fontSize: '13px' }}>
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
        </select>

        <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => load(page)} title="Refresh">
          <i className="fa-solid fa-arrows-rotate" />
        </button>
      </div>

      <DataTable
        columns={columns}
        data={logs}
        loading={loading}
        onRowClick={openPreview}
        emptyMessage="No email logs found"
        emptyIcon="fa-solid fa-envelope-open"
      />

      <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--admin-text-muted)' }}>
        {total} total log{total !== 1 ? 's' : ''} — click any row to preview
      </div>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={(p) => { setPage(p); load(p); }} />


      {/* Preview Modal */}
      {preview && (
        <EmailPreviewModal
          log={previewLoading ? { ...preview, html_body: null } : preview}
          onClose={() => setPreview(null)}
          onRetry={(row) => handleRetry(row)}
          retrying={retrying === preview?.id}
        />
      )}
    </>
  );
}

export default AdminEmailLogs;
